import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
  onAuthStateChanged,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth, db, handleFirestoreError, OperationType } from './firebase';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

export interface AppUser {
  uid: string;
  name: string;
  email: string;
  photoURL?: string;
  avatar?: string;
  phone?: string;
  address?: string;
  country?: string;
  emailVerified: boolean;
  createdAt?: any;
  lastLogin?: any;
  memberSince?: string;
  apiKey?: string;
  role?: 'customer' | 'admin';
}

export function isKnownAdminEmail(email?: string | null): boolean {
  if (!email) return false;
  const clean = email.toLowerCase().trim();
  return (
    clean === 'mrvictorists@gmail.com' ||
    clean === 'mrvictorist@gmail.com' ||
    clean === 'rh.editography@gmail.com' ||
    clean === 'admin@rheditography.net' ||
    clean === 'support@rheditography.net'
  );
}

const DEFAULT_AVATAR = `https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80`;

// In-Memory & LocalStorage User Cache
let cachedAppUser: AppUser | null = null;
let lastSyncedUid: string | null = null;
let lastSyncTimestamp = 0;

try {
  const saved = localStorage.getItem('rh_current_user');
  if (saved) {
    cachedAppUser = JSON.parse(saved);
  }
} catch {}

export function getCachedAppUser(): AppUser | null {
  return cachedAppUser;
}

function updateCache(user: AppUser | null) {
  cachedAppUser = user;
  try {
    if (user) {
      localStorage.setItem('rh_user_logged_in', 'true');
      localStorage.setItem('rh_current_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('rh_user_logged_in');
      localStorage.removeItem('rh_current_user');
    }
  } catch {}
}

function buildFastAppUser(user: FirebaseUser, displayNameOverride?: string): AppUser {
  const existing = cachedAppUser && cachedAppUser.uid === user.uid ? cachedAppUser : null;
  const name = displayNameOverride || user.displayName || existing?.name || user.email?.split('@')[0] || 'Member';
  const photoURL = user.photoURL || existing?.photoURL || existing?.avatar || DEFAULT_AVATAR;
  const isAdmin = isKnownAdminEmail(user.email);
  const role: 'customer' | 'admin' = existing?.role === 'admin' || isAdmin ? 'admin' : (existing?.role || 'customer');
  const apiKey = existing?.apiKey || `rh_live_${user.uid.substring(0, 8)}_${Math.random().toString(36).substring(2, 8)}`;
  const memberSince = existing?.memberSince || new Date().toISOString().split('T')[0];

  return {
    uid: user.uid,
    name,
    email: user.email || '',
    photoURL,
    avatar: photoURL,
    phone: existing?.phone,
    address: existing?.address,
    country: existing?.country,
    emailVerified: user.emailVerified,
    memberSince,
    apiKey,
    role,
  };
}

// Subscribers management
const subscribers = new Set<(user: AppUser | null) => void>();

function notifySubscribers(user: AppUser | null) {
  subscribers.forEach((cb) => {
    try {
      cb(user);
    } catch (e) {
      console.error('Subscriber notification error:', e);
    }
  });
}

// Save or sync user profile in Firestore (Optimized for instant return)
export async function syncUserProfile(user: FirebaseUser, displayNameOverride?: string): Promise<AppUser> {
  const fastUser = buildFastAppUser(user, displayNameOverride);
  updateCache(fastUser);

  const now = Date.now();
  // Skip duplicate Firestore network reads if synced within the last 5 seconds for same UID
  // UNLESS displayNameOverride is explicitly passed (e.g. fresh registration or explicit update)
  if (!displayNameOverride && lastSyncedUid === user.uid && now - lastSyncTimestamp < 5000) {
    return fastUser;
  }

  lastSyncedUid = user.uid;
  lastSyncTimestamp = now;

  // Immediate Sync with Firestore (Combines blocked check and full sync)
  const userRef = doc(db, 'users', user.uid);
  let finalUser = fastUser;
  
  try {
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      const data = snap.data();
      if (data.status === 'Blocked' || data.accountLocked) {
        await firebaseSignOut(auth);
        throw new Error('Your account has been locked or blocked by the administrator.');
      }

      finalUser = {
        ...fastUser,
        name: displayNameOverride || data.name || data.displayName || fastUser.name,
        memberSince: data.memberSince || fastUser.memberSince,
        photoURL: data.photoURL || data.avatar || fastUser.photoURL,
        avatar: data.photoURL || data.avatar || fastUser.avatar,
        phone: data.phone || fastUser.phone,
        address: data.address || fastUser.address,
        country: data.country || fastUser.country,
        role: isKnownAdminEmail(user.email) ? 'admin' : (data.role || fastUser.role),
        apiKey: data.apiKey || fastUser.apiKey,
        emailVerified: (data.emailVerified === true) ? true : fastUser.emailVerified,
      };

      updateDoc(userRef, {
        name: finalUser.name,
        email: user.email,
        photoURL: finalUser.photoURL,
        role: finalUser.role,
        isAdmin: finalUser.role === 'admin',
        emailVerified: finalUser.emailVerified, // preserves true if it was already true
        lastLogin: serverTimestamp(),
      }).catch((err) => console.warn('Background lastLogin update note:', err));

    } else {
      // Create initial document in Firestore for new user
      const initialRole = isKnownAdminEmail(user.email) ? 'admin' : fastUser.role;
      await setDoc(userRef, {
        uid: user.uid,
        name: fastUser.name,
        email: user.email,
        photoURL: fastUser.photoURL,
        emailVerified: user.emailVerified,
        memberSince: fastUser.memberSince,
        apiKey: fastUser.apiKey,
        role: initialRole,
        isAdmin: initialRole === 'admin',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
      });
      finalUser.role = initialRole;
    }
  } catch (err: any) {
    if (err.message === 'Your account has been locked or blocked by the administrator.') {
      throw err;
    }
    console.warn('Firestore profile sync note:', err);
  }

  if (isKnownAdminEmail(user.email)) {
    finalUser.role = 'admin';
  }

  try {
    const idToken = await user.getIdTokenResult();
    if (idToken.claims && idToken.claims.admin === true) {
      finalUser.role = 'admin';
    }
  } catch {}

  updateCache(finalUser);
  notifySubscribers(finalUser);
  return finalUser;
}

// Update user profile in Firestore and Firebase Auth
export async function updateUserProfileInFirestore(
  uid: string,
  data: { name?: string; email?: string; avatar?: string; phone?: string; address?: string; country?: string }
): Promise<void> {
  const userRef = doc(db, 'users', uid);
  try {
    if (auth.currentUser && data.name) {
      updateProfile(auth.currentUser, { displayName: data.name }).catch(() => {});
    }
    const updatePayload: any = {};
    if (data.name !== undefined) updatePayload.name = data.name;
    if (data.email !== undefined) updatePayload.email = data.email;
    if (data.phone !== undefined) updatePayload.phone = data.phone;
    if (data.address !== undefined) updatePayload.address = data.address;
    if (data.country !== undefined) updatePayload.country = data.country;
    if (data.avatar) {
      updatePayload.avatar = data.avatar;
      updatePayload.photoURL = data.avatar;
    }
    await setDoc(userRef, updatePayload, { merge: true });
    if (auth.currentUser) {
      const synced = await syncUserProfile(auth.currentUser, data.name);
      const fullUser: AppUser = {
        ...synced,
        phone: data.phone ?? synced.phone,
        address: data.address ?? synced.address,
        country: data.country ?? synced.country,
        avatar: data.avatar ?? synced.avatar,
        photoURL: data.avatar ?? synced.photoURL
      };
      updateCache(fullUser);
      notifySubscribers(fullUser);
    }
  } catch (err) {
    handleFirestoreError(err, OperationType.UPDATE, `users/${uid}`);
  }
}

function formatFirebaseAuthError(err: any): Error {
  const code = err?.code || '';
  switch (code) {
    case 'auth/email-already-in-use':
      return new Error('This email address is already registered. Please login instead.');
    case 'auth/invalid-email':
      return new Error('The email address entered is invalid.');
    case 'auth/weak-password':
      return new Error('Password should be at least 6 characters long.');
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return new Error('Invalid email or password. Please check your credentials.');
    case 'auth/popup-closed-by-user':
      return new Error('Sign-in popup was closed before completing authentication.');
    case 'auth/too-many-requests':
      return new Error('Too many failed attempts. Please try again later.');
    case 'auth/operation-not-allowed':
      return new Error('This authentication provider is not enabled in Firebase Console.');
    default:
      return new Error(err?.message || 'Authentication error occurred.');
  }
}

// 1. REGISTER
export async function registerUser(name: string, email: string, pass: string): Promise<AppUser> {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, pass);
    if (cred.user) {
      updateProfile(cred.user, { displayName: name }).catch(() => {});
      return await syncUserProfile(cred.user, name);
    }
    throw new Error('Registration failed');
  } catch (err: any) {
    throw formatFirebaseAuthError(err);
  }
}

// 2. LOGIN (Optimized - Instant return with Password Sync fallback)
export async function loginUser(email: string, pass: string): Promise<AppUser> {
  const cleanEmail = email.trim().toLowerCase();
  try {
    const cred = await signInWithEmailAndPassword(auth, cleanEmail, pass);
    if (cred.user) {
      return await syncUserProfile(cred.user);
    }
    throw new Error('Sign in failed');
  } catch (err: any) {
    const firebaseErrCode = err?.code || '';
    if (
      firebaseErrCode === 'auth/wrong-password' ||
      firebaseErrCode === 'auth/invalid-credential' ||
      firebaseErrCode === 'auth/user-not-found'
    ) {
      // Removed plaintext password sync fallback
    }
    throw formatFirebaseAuthError(err);
  }
}

// 3. GOOGLE SIGN IN
export async function signInWithGoogle(): Promise<AppUser> {
  try {
    const provider = new GoogleAuthProvider();
    const res = await signInWithPopup(auth, provider);
    if (res.user) {
      return await syncUserProfile(res.user);
    }
    throw new Error('Google Sign In failed');
  } catch (err: any) {
    throw formatFirebaseAuthError(err);
  }
}

// 4. RESET PASSWORD VIA ENTERPRISE OTP
export async function resetUserPassword(email: string): Promise<void> {
  try {
    const { sendEmailOTP } = await import('./otpService');
    const res = await sendEmailOTP(email, 'FORGOT_PASSWORD');
    if (!res.success) {
      throw new Error(res.message);
    }
  } catch (err: any) {
    throw formatFirebaseAuthError(err);
  }
}

// 5. RESEND VERIFICATION EMAIL VIA ENTERPRISE OTP
export async function resendVerificationEmail(): Promise<void> {
  if (auth.currentUser && auth.currentUser.email) {
    const { sendEmailOTP } = await import('./otpService');
    const res = await sendEmailOTP(auth.currentUser.email, 'VERIFY_EMAIL', auth.currentUser.displayName || undefined);
    if (!res.success) {
      throw new Error(res.message);
    }
  } else {
    throw new Error('No active user session to send verification code.');
  }
}

// 6. CHECK AND REFRESH EMAIL VERIFICATION STATUS
export async function checkAndRefreshEmailVerification(): Promise<boolean> {
  if (!auth.currentUser) return false;
  await auth.currentUser.reload();
  const isVerified = auth.currentUser.emailVerified;
  if (isVerified) {
    const appUser = await syncUserProfile(auth.currentUser);
    notifySubscribers(appUser);
  }
  return isVerified;
}

// Mark user email as verified locally and sync to Firestore
export function markUserEmailVerifiedLocally(uid?: string): AppUser | null {
  if (cachedAppUser) {
    cachedAppUser = {
      ...cachedAppUser,
      emailVerified: true,
    };
    updateCache(cachedAppUser);
    notifySubscribers(cachedAppUser);

    const targetUid = cachedAppUser.uid || uid;
    if (targetUid) {
      const userRef = doc(db, 'users', targetUid);
      updateDoc(userRef, { emailVerified: true }).catch(() => {});
    }
    return cachedAppUser;
  }
  return null;
}

// 7. LOGOUT
export async function logoutUser(): Promise<void> {
  updateCache(null);
  notifySubscribers(null);
  await firebaseSignOut(auth);
}

// 8. OBSERVE AUTH CHANGES (Centralized & Deduplicated)
let isListenerInitialized = false;

export function subscribeToAuthChanges(callback: (user: AppUser | null) => void) {
  subscribers.add(callback);

  // Immediately inform subscriber with cached data if available
  callback(cachedAppUser);

  if (!isListenerInitialized) {
    isListenerInitialized = true;
    onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        syncUserProfile(firebaseUser).then((appUser) => {
          notifySubscribers(appUser);
        });
      } else {
        updateCache(null);
        notifySubscribers(null);
      }
    });
  }

  return () => {
    subscribers.delete(callback);
  };
}
